import { Box } from "@mui/material";
import type {
	ColDef,
	GetRowIdParams,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo } from "react";
import {
	globalDefaultColDef,
	mesfColors,
	mesfTheme,
} from "@/setup/ag-grid/mesf-theme";
import type { GroupRowBase, StandCell } from "../models/InformationRetrieval";
import { isOutOfRange } from "../utils/aggregate";

const DASH = "--------";

/**
 * The app installs its theme through `provideGlobalGridOptions`, so AG Grid v33
 * generates its own CSS and plain overrides lose to it. The focus ring that
 * stays lit on every ticked checkbox — the hidden input keeps DOM focus after a
 * click — has to be turned off through theme params, not stylesheet rules.
 * Scoped to this grid; cell focus is already suppressed, so nothing is lost.
 */
const groupsGridTheme = mesfTheme.withParams({
	focusShadow: "none",
	inputFocusShadow: "none",
	// Every row starts selected, so AG Grid's default selected-row blue paints
	// the whole grid and reads as "stuck in focus". The checkbox already says
	// what is selected; the row only needs a hint.
	selectedRowBackgroundColor: "rgba(45,49,66,0.035)",
});

/**
 * Width of AG Grid's selection column, and the size the Material theme draws a
 * checkbox at. Both are needed because the box has to be centred by hand — see
 * the margin override on the grid below.
 */
const SELECTION_COL_WIDTH = 48;
const CHECKBOX_SIZE = 18;
const CHECKBOX_INSET = (SELECTION_COL_WIDTH - CHECKBOX_SIZE) / 2;

const format3 = (value: number | null | undefined): string =>
	value === null || value === undefined ? DASH : Number(value).toFixed(3);

/** The four stacked numbers, top to bottom, and what each one means. */
const LINES = [
	{ key: "avg", label: "Actual", hint: "R-Factor the stand really ran" },
	{ key: "target", label: "Recipe", hint: "target from the recipe" },
	{ key: "min", label: "Min", hint: "recipe tolerance, lower bound" },
	{ key: "max", label: "Max", hint: "recipe tolerance, upper bound" },
] as const;

interface Props {
	groups: GroupRowBase[];
	stands: string[];
	/** Header of the pinned column: "Stand" on the mill, "Roll" on the straightener. */
	columnLabel?: string;
	/** What the first stacked number is, for the cell tooltip. */
	actualHint?: string;
	/** Tooltip on a column with no data for the group. */
	emptyCellHint?: string;
	onSelectionChange: (keys: string[]) => void;
	/** Group colours only mean something while the chart draws a line per group. */
	showLegendColor: boolean;
}

const StandCellRenderer = ({
	value,
	standName,
	actualHint,
	emptyCellHint,
}: {
	value?: StandCell;
	standName: string;
	actualHint: string;
	emptyCellHint: string;
}) => {
	const active = value?.active === true;
	const outOfRange = active && isOutOfRange(value.avg, value.min, value.max);

	// Native title rather than a MUI tooltip: this renders once per stand per
	// row, and the browser one costs nothing and never fights the grid.
	const title = active
		? `${standName}\n${LINES.map(
				(l) =>
					`${l.label}  ${format3(value[l.key])}  — ${l.key === "avg" ? actualHint : l.hint}`,
			).join("\n")}`
		: `${standName}\n${emptyCellHint}`;

	return (
		<Box
			title={title}
			sx={{
				textAlign: "center",
				lineHeight: 1.25,
				py: 0.25,
				width: "100%",
				...(active ? {} : { color: mesfColors.text.secondary }),
			}}
		>
			<Box
				sx={{
					fontWeight: 700,
					fontSize: 13,
					color: outOfRange
						? mesfColors.error.main
						: active
							? mesfColors.text.primary
							: "inherit",
				}}
			>
				{active ? format3(value.avg) : DASH}
			</Box>
			<Box sx={{ fontSize: 11 }}>{active ? format3(value.target) : DASH}</Box>
			<Box sx={{ fontSize: 10, color: mesfColors.text.secondary }}>
				{active ? format3(value.min) : DASH}
			</Box>
			<Box sx={{ fontSize: 10, color: mesfColors.text.secondary }}>
				{active ? format3(value.max) : DASH}
			</Box>
		</Box>
	);
};

/**
 * The label column doubles as the key to the four stacked numbers, so the cells
 * do not depend on anyone hovering to be readable.
 */
const LabelRenderer = ({
	data,
	showLegendColor,
}: {
	data?: GroupRowBase;
	showLegendColor: boolean;
}) => {
	if (!data) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.25 }}>
			{showLegendColor ? (
				<Box
					component="span"
					sx={{
						width: 10,
						height: 10,
						borderRadius: "50%",
						backgroundColor: data.legendColor,
						border: "1px solid #ACA8A8",
						flexShrink: 0,
					}}
				/>
			) : null}
			<Box>
				<Box sx={{ fontWeight: 600, fontSize: 12 }}>{data.label}</Box>
				<Box
					sx={{
						fontSize: 9,
						color: mesfColors.text.secondary,
						lineHeight: 1.3,
					}}
				>
					actual · recipe · min · max
				</Box>
			</Box>
		</Box>
	);
};

export const GroupsGrid = ({
	groups,
	stands,
	columnLabel = "Stand",
	actualHint = "R-Factor the stand really ran",
	emptyCellHint = "This stand produced nothing in this shift",
	onSelectionChange,
	showLegendColor,
}: Props) => {
	const columnDefs = useMemo<ColDef<GroupRowBase>[]>(() => {
		const labelCol: ColDef<GroupRowBase> = {
			colId: "label",
			headerName: columnLabel,
			pinned: "left",
			width: 170,
			minWidth: 170,
			sortable: false,
			resizable: false,
			suppressMovable: true,
			valueGetter: (params) => params.data?.label,
			cellRenderer: (params: { data?: GroupRowBase }) => (
				<LabelRenderer data={params.data} showLegendColor={showLegendColor} />
			),
			cellStyle: { display: "flex", alignItems: "center" },
			headerStyle: { fontWeight: 700, fontSize: 13 },
		};

		const standCols = stands.map<ColDef<GroupRowBase>>((name) => ({
			colId: name,
			headerName: name,
			valueGetter: (params) => params.data?.stands[name],
			cellRenderer: (params: { value?: StandCell }) => (
				<StandCellRenderer
					value={params.value}
					standName={name}
					actualHint={actualHint}
					emptyCellHint={emptyCellHint}
				/>
			),
			flex: 1,
			minWidth: 76,
			sortable: false,
			resizable: false,
			cellStyle: { display: "flex", alignItems: "center", padding: 0 },
			headerStyle: { fontWeight: 700, fontSize: 13 },
		}));

		return [labelCol, ...standCols];
	}, [stands, showLegendColor, columnLabel, actualHint, emptyCellHint]);

	const pushSelection = useCallback(
		(event: SelectionChangedEvent<GroupRowBase>) => {
			onSelectionChange(
				event.api.getSelectedRows().map((row: GroupRowBase) => row.key),
			);
		},
		[onSelectionChange],
	);

	// Every fetch starts with the whole result selected, as the legacy screen
	// does — the aggregate grid and the chart read from this selection.
	const handleGridReady = useCallback((event: GridReadyEvent<GroupRowBase>) => {
		event.api.selectAll();
	}, []);

	const getRowId = (params: GetRowIdParams<GroupRowBase>) => params.data.key;

	if (groups.length === 0) return null;

	return (
		<Box
			sx={{
				height: Math.min(groups.length * 78 + 34, 280),
				// The theme sets a right margin on the checkbox for cells where it sits
				// beside content. Here it owns the column, so the margin only pushes it
				// off centre against the header one.
				"& .ag-ltr .ag-selection-checkbox, & .ag-selection-checkbox, & .ag-header-select-all":
					{
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
						margin: 0,
					},
				// The app theme insets every checkbox with a hard-coded left margin —
				// 9px in a header, 12px in a cell — which is what left the select-all
				// sitting 3px to the left of the row boxes. Here the checkbox owns the
				// column, so both take the same inset and land on its centre line.
				"& .ag-header-select-all .ag-checkbox-input-wrapper, & .ag-selection-checkbox .ag-checkbox-input-wrapper":
					{
						marginLeft: `${CHECKBOX_INSET}px !important`,
					},
			}}
		>
			<AgGridReact<GroupRowBase>
				theme={groupsGridTheme}
				rowData={groups}
				columnDefs={columnDefs}
				// `globalDefaultColDef` turns on autoHeight, which makes AG Grid size
				// rows to their content and ignore `rowHeight` — that is what left the
				// checkboxes sitting off-centre against the header one.
				defaultColDef={{
					...globalDefaultColDef,
					resizable: false,
					autoHeight: false,
					wrapText: false,
				}}
				headerHeight={30}
				rowHeight={78}
				getRowId={getRowId}
				// Native multi-row selection. A hand-rolled checkbox in a header
				// component does not work: AG Grid does not re-render header
				// components when outside state changes, so it keeps the first
				// closure and the click does nothing.
				rowSelection={{
					mode: "multiRow",
					checkboxes: true,
					headerCheckbox: true,
					enableClickSelection: false,
				}}
				selectionColumnDef={{
					width: SELECTION_COL_WIDTH,
					minWidth: SELECTION_COL_WIDTH,
					maxWidth: SELECTION_COL_WIDTH,
					pinned: "left",
					resizable: false,
					suppressMovable: true,
					// No padding here: the theme zeroes cell and header padding with
					// `!important`, so the inset is the checkbox margin above.
					cellStyle: {
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
					},
					headerStyle: {
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
					},
				}}
				onGridReady={handleGridReady}
				onFirstDataRendered={(event) => event.api.selectAll()}
				onSelectionChanged={pushSelection}
				suppressCellFocus
				suppressContextMenu
				suppressColumnVirtualisation
				suppressRowHoverHighlight
				animateRows={false}
			/>
		</Box>
	);
};
